import React, { useEffect, useState } from 'react';
import { Player } from '@/types';
import { wyscoutMatchesAPI, reportsFeedAPI } from '@/services/api';
import FormationPitch from '@/components/team/FormationPitch';
import { mapWyscoutLineup, MappingResult } from '@/utils/wyscoutFormationMapper';

interface LineupPlayer {
  playerId: number;
  shirtNumber?: number;
  position: string;
}

interface Substitution {
  playerIn: number;
  playerOut: number;
  minute: number;
}

interface Props {
  formationSchema: string;
  lineup: LineupPlayer[];
  bench: LineupPlayer[];
  substitutions: Substitution[];
  teamName: string;
  matchDate?: string; // YYYY-MM-DD for fetching performance reports
  disablePerformanceReportRatings?: boolean;
  resolvePlayers?: boolean;
  playerNames?: ReadonlyMap<number, string>;
}

interface Enrichment {
  key: string;
  players: Map<number, Player>;
  names: Map<number, string>;
  ratings: Map<number, number>;
}

const playerLabel = (player: LineupPlayer): string => (
  player.shirtNumber === undefined ? 'Unknown player' : `#${player.shirtNumber}`
);

const syntheticPlayer = (player: LineupPlayer): Player => ({
  id: `wyscout-${player.playerId}`,
  name: playerLabel(player),
  age: 0,
  position: 'Forward',
  createdBy: '',
  createdAt: '',
  updatedAt: '',
  wyscoutPlayerId: player.playerId,
});

const MatchFormationPitch: React.FC<Props> = ({
  formationSchema,
  lineup,
  bench,
  substitutions,
  teamName,
  matchDate,
  disablePerformanceReportRatings = false,
  resolvePlayers = true,
  playerNames: suppliedPlayerNames,
}) => {
  const inputKey = JSON.stringify({ formationSchema, lineup, bench, substitutions, matchDate, disablePerformanceReportRatings, resolvePlayers });
  const [enrichment, setEnrichment] = useState<Enrichment | null>(null);

  const playerLookup = new Map<number, Player>();
  const displayNames = new Map<number, string>();
  for (const player of [...lineup, ...bench]) {
    const label = playerLabel(player);
    playerLookup.set(player.playerId, syntheticPlayer(player));
    displayNames.set(player.playerId, label);
  }
  if (resolvePlayers && enrichment?.key === inputKey) {
    enrichment.players.forEach((player, id) => playerLookup.set(id, player));
    enrichment.names.forEach((name, id) => displayNames.set(id, name));
  }
  suppliedPlayerNames?.forEach((name, id) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    displayNames.set(id, trimmed);
    const player = playerLookup.get(id);
    if (player) playerLookup.set(id, { ...player, name: trimmed });
  });
  const playerRatings = resolvePlayers && enrichment?.key === inputKey ? enrichment.ratings : new Map<number, number>();
  const mapping: MappingResult | null = mapWyscoutLineup(formationSchema, lineup, playerLookup);

  useEffect(() => {
    if (!resolvePlayers) return;

    let cancelled = false;

    const resolve = async () => {
      // Collect all player IDs (lineup + bench + substitution ins)
      const allPlayers = [
        ...lineup.map((p) => ({
          wyscoutPlayerId: p.playerId,
          name: playerLabel(p),
          shortName: playerLabel(p),
          position: p.position,
        })),
        ...bench.map((p) => ({
          wyscoutPlayerId: p.playerId,
          name: playerLabel(p),
          shortName: playerLabel(p),
          position: p.position,
        })),
      ];

      // Add substitution playerIn IDs that aren't in lineup/bench
      const knownIds = new Set(allPlayers.map((p) => p.wyscoutPlayerId));
      for (const sub of substitutions) {
        if (!knownIds.has(sub.playerIn)) {
          allPlayers.push({
            wyscoutPlayerId: sub.playerIn,
            name: 'Unknown player',
            shortName: 'Unknown player',
            position: 'SUB',
          });
          knownIds.add(sub.playerIn);
        }
      }

      const resolvedPlayers = new Map<number, Player>();
      const names = new Map<number, string>();

      try {
        const resolved = await wyscoutMatchesAPI.resolvePlayers(allPlayers);
        if (cancelled) return;
        if (resolved.data) {
          // Load player details for resolved IDs
          const entries = Object.entries(resolved.data);
          // Build lookup: wyscoutPlayerId -> Player
          for (const [wyscoutIdStr, info] of entries) {
            const wyscoutId = Number(wyscoutIdStr);
            if (!Number.isSafeInteger(wyscoutId) || wyscoutId <= 0) continue;
            const resolvedName = info.name || allPlayers.find((p) => p.wyscoutPlayerId === wyscoutId)?.name || 'Unknown player';
            const syntheticPlayer: Player = {
              id: info.playerId,
              name: resolvedName,
              age: 0,
              position: 'Forward',
              createdBy: '',
              createdAt: '',
              updatedAt: '',
              wyscoutPlayerId: wyscoutId,
            };
            resolvedPlayers.set(wyscoutId, syntheticPlayer);
            names.set(wyscoutId, resolvedName);
          }
        }
      } catch {
        // If resolve fails, create synthetic players from lineup data
      }

      if (cancelled) return;

      // Fetch performance reports for this match date to get ratings
      const ratingsMap = new Map<number, number>();
      if (matchDate && !disablePerformanceReportRatings) {
        try {
          const feedRes = await reportsFeedAPI.list({
            type: 'performance',
            startDate: matchDate,
            endDate: matchDate + 'T23:59:59Z',
            limit: 200,
          });
          if (cancelled) return;
          if (feedRes.data) {
            // Build internalPlayerId -> rating map (only for reports whose date matches this match)
            const internalToRating = new Map<string, number>();
            for (const item of feedRes.data) {
              if (item.rating != null && item.date && item.date.startsWith(matchDate)) {
                internalToRating.set(item.playerId, item.rating);
              }
            }
            // Map internal player IDs back to wyscout player IDs
            resolvedPlayers.forEach((player, wyscoutId) => {
              const rating = internalToRating.get(player.id);
              if (rating != null) {
                ratingsMap.set(wyscoutId, rating);
              }
            });
          }
        } catch {
          // Non-critical - just don't show ratings
        }
      }

      if (!cancelled) {
        setEnrichment({ key: inputKey, players: resolvedPlayers, names, ratings: ratingsMap });
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [inputKey, formationSchema, lineup, bench, substitutions, matchDate, disablePerformanceReportRatings, resolvePlayers]);

  const getPlayerName = (id: number) => displayNames.get(id) || 'Unknown player';

  if (!mapping) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
        Formation "{formationSchema}" not available for pitch view
      </div>
    );
  }

  // Build positionId -> rating map for the pitch
  const positionRatings: Record<string, number> = {};
  if (mapping && playerRatings.size > 0) {
    for (const [posId, player] of Object.entries(mapping.positionMap)) {
      if (player?.wyscoutPlayerId) {
        const rating = playerRatings.get(player.wyscoutPlayerId);
        if (rating != null) positionRatings[posId] = rating;
      }
    }
  }

  const ratingBadge = (rating: number) => {
    let color: string;
    if (rating >= 70) {
      color = 'bg-green-500';
    } else if (rating >= 50) {
      color = 'bg-yellow-500';
    } else {
      color = 'bg-red-500';
    }
    return (
      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-white text-[9px] font-bold ${color}`}>
        {rating}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-2 text-center">
        <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
          {teamName} - {formationSchema}
        </span>
      </div>
      <div className="max-w-md mx-auto">
        <FormationPitch
          formation={mapping.formation}
          selected={mapping.positionMap}
          ratings={Object.keys(positionRatings).length > 0 ? positionRatings : undefined}
        />
      </div>

      {/* Substitutions */}
      {substitutions.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Substitutions</h4>
          <div className="flex flex-wrap gap-2">
            {substitutions.map((sub, idx) => (
              <div key={idx} className="flex items-center gap-1 text-xs bg-gray-50 dark:bg-gray-800 rounded px-2 py-1">
                <span className="text-green-600 dark:text-green-400">{getPlayerName(sub.playerIn)}</span>
                <svg className="h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span className="text-red-600 dark:text-red-400">{getPlayerName(sub.playerOut)}</span>
                <span className="text-gray-400 dark:text-gray-500">({sub.minute}')</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bench */}
      {bench.length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bench</h4>
          <div className="flex flex-wrap gap-2">
            {bench.map((p, idx) => {
              const rating = playerRatings.get(p.playerId);
              return (
                <span key={p.playerId} className="inline-flex items-center gap-1 text-xs bg-gray-50 dark:bg-gray-800 rounded px-2 py-1 text-gray-700 dark:text-gray-300">
                  {getPlayerName(p.playerId)}{p.position.trim() && ` (${p.position.trim()})`}
                  {rating != null && ratingBadge(rating)}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchFormationPitch;
